import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { modalVariants } from "../Utils/constants";

import { ArrowLeft } from "../Assets/Icons";
import CloseButton from "../Components/Buttons/close-button";

const ModalContext = createContext();

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button, input:not([type="hidden"]), select, textarea, ' +
  'iframe, audio[controls], video[controls], [contenteditable=""], [contenteditable="true"], [tabindex]';

const getFocusableElements = (root) => {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    if (el.getAttribute("tabindex") === "-1") return false;
    if (el.offsetWidth === 0 && el.offsetHeight === 0 && !el.getClientRects().length) return false;
    return true;
  });
};

const defaultAriaLabelFor = (variant) => {
  switch (variant) {
    case modalVariants.promptDialog: return "Confirmation prompt";
    case modalVariants.statusDialog: return "Status update";
    case modalVariants.tooltipDialog: return "More information";
    case modalVariants.formDialog: return "Form dialog";
    case modalVariants.stepByStepDialog: return "Multi-step dialog";
    case modalVariants.fullScreenDialog: return "Dialog";
    case modalVariants.flexDialog: return "Dialog";
    case modalVariants.infoDialog:
    default: return "Dialog";
  }
};

export const ModalContextProvider = ({ children }) => {
  const [modalVisibility, setModalVisibility] = useState(false);
  const [modalClassName, setModalClassName] = useState(null);
  const [preventCloseOnBackdropClick, setPreventCloseOnBackdropClick] = useState(false);
  const [ModalComponent, setModalComponent] = useState(() => React.Fragment);

  const [modalVariant, setModalVariant] = useState(modalVariants.infoDialog);
  const [modalTitle, setModalTitle] = useState("");
  const [modalAriaLabel, setModalAriaLabel] = useState("");
  const [modalStep, setModalStep] = useState(1);
  const [ModalPromoComponent, setModalPromoComponent] = useState(null);
  const [modalOnCloseCallBack, setModalOnCloseCallBack] = useState(null);
  const [modalFormDefaultValues, setModalFormDefaultValues] = useState({});

  const [modalStack, setModalStack] = useState([]);

  const removeComponentTimeoutRef = useRef(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const refocusTimeoutRef = useRef(null);

  const formProps = useForm({ values: modalFormDefaultValues, resetOptions: { keepDirtyValues: true } });

  const focusInitialElementInModal = useCallback(() => {
    if (!modalRef.current) return;
    const headerHidden =
      (modalClassName || "").includes("hide-header") ||
      (modalClassName || "").includes("setup-stake-pay-2");

    let target = null;
    if (!headerHidden) {
      target = modalRef.current.querySelector(".modal-dialog-header .close-button");
    }
    if (!target && headerHidden) {
      target =
        modalRef.current.querySelector(".sc-ob-close-btn") ||
        modalRef.current.querySelector('[aria-label="Close"]');
    }
    if (!target) {
      const focusables = getFocusableElements(modalRef.current);
      target = focusables[0] || null;
    }
    if (!target) {
      const modalPanel = modalRef.current.querySelector(".cb-modal");
      if (modalPanel) {
        modalPanel.setAttribute("tabindex", "-1");
        target = modalPanel;
      } else {
        modalRef.current.setAttribute("tabindex", "-1");
        target = modalRef.current;
      }
    }
    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus?.();
    }
  }, [modalClassName]);

  const updateModalComponent = (Component) => {
    clearTimeout(removeComponentTimeoutRef?.current);
    removeComponentTimeoutRef.current = null;
    setModalComponent(() => Component);
    setModalVisibility(true);
  };

  const removeModalComponent = () => {
    setModalComponent(() => () => React.Fragment);
  };

  const pushModalStack = (renderCallback) => {
    setModalStack((p) => {
      return [...p, renderCallback];
    });
  };

  const popModalStack = (i) => {
    const modalRenderCallbackAtPopIndex = modalStack[modalStack.length - i - 1];
    if (!!modalRenderCallbackAtPopIndex) {
      modalRenderCallbackAtPopIndex();
    } else {
      onModalClose();
    }

    setModalStack((p) => p.slice(0, modalStack.length - i));
  };

  const updateModalFormDefaultValues = (defaultValues) =>
    setModalFormDefaultValues((p) => ({ ...p, ...defaultValues }));

  const updateModalOnCloseCallBack = (callback) => setModalOnCloseCallBack(() => callback);

  const onBackButtonClick = () => {
    if (modalVariant === modalVariants.stepByStepDialog && modalStep > 1) {
      setModalStep((step) => step - 1);
    } else if (modalStack.length > 1) {
      popModalStack(1);
    }
  };

  const onModalClose = useCallback(({ callOnClose, abortCallingOnCloseCallback = false } = {}) => {
    if (!!modalOnCloseCallBack && !abortCallingOnCloseCallback) {
      modalOnCloseCallBack();
    }

    if (!!callOnClose) {
      callOnClose();
    }

    setModalVisibility(false);

    removeComponentTimeoutRef.current = setTimeout(() => {
      removeModalComponent();
      setModalStack([]);
      setModalClassName(null);
      setPreventCloseOnBackdropClick(false);
      setModalVariant(modalVariants.infoDialog);
      setModalTitle("");
      setModalAriaLabel("");
      setModalStep(1);
      setModalPromoComponent(null);
      setModalOnCloseCallBack(null);
      setModalFormDefaultValues({});
      formProps.reset();
      removeComponentTimeoutRef.current = null;
      document.body.style.overflow = null;
    }, 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOnCloseCallBack]);

  useEffect(() => {
    if (modalVisibility) {
      document.body.style.overflow = "hidden";
    }
  }, [modalVisibility]);

  useEffect(() => {
    if (modalVisibility) {
      const active = document.activeElement;
      if (active && active !== document.body) {
        previousFocusRef.current = active;
      }
      clearTimeout(refocusTimeoutRef.current);
      refocusTimeoutRef.current = setTimeout(() => {
        focusInitialElementInModal();
      }, 60);
    } else {
      clearTimeout(refocusTimeoutRef.current);
      const toRestore = previousFocusRef.current;
      previousFocusRef.current = null;
      if (toRestore && typeof toRestore.focus === "function" && document.body.contains(toRestore)) {
        try {
          toRestore.focus({ preventScroll: true });
        } catch {
          toRestore.focus?.();
        }
      }
    }
    return () => clearTimeout(refocusTimeoutRef.current);
  }, [modalVisibility, focusInitialElementInModal]);

  useEffect(() => {
    if (!modalVisibility) return;
    clearTimeout(refocusTimeoutRef.current);
    refocusTimeoutRef.current = setTimeout(() => {
      focusInitialElementInModal();
    }, 60);
    return () => clearTimeout(refocusTimeoutRef.current);
  }, [ModalComponent, modalStep, modalVisibility, focusInitialElementInModal]);

  useEffect(() => {
    if (!modalVisibility) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onModalClose();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusables = getFocusableElements(modalRef.current);
      if (focusables.length === 0) {
        e.preventDefault();
        try { modalRef.current.focus({ preventScroll: true }); } catch { /* noop */ }
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const insideModal = modalRef.current.contains(active);
      if (e.shiftKey) {
        if (!insideModal || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!insideModal || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modalVisibility, onModalClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !preventCloseOnBackdropClick) {
      onModalClose();
    }
  };

  return (
    <ModalContext.Provider
      value={{
        modalVisibility,
        setModalVisibility,
        modalClassName,
        setModalClassName,
        preventCloseOnBackdropClick,
        setPreventCloseOnBackdropClick,
        modalVariant,
        setModalVariant,
        modalTitle,
        setModalTitle,
        modalAriaLabel,
        setModalAriaLabel,
        ModalComponent,
        updateModalComponent,
        removeModalComponent,
        onModalClose,
        modalStep,
        setModalStep,
        ModalPromoComponent,
        setModalPromoComponent,
        modalOnCloseCallBack,
        setModalOnCloseCallBack,
        updateModalOnCloseCallBack,
        modalStack,
        setModalStack,
        pushModalStack,
        popModalStack,
        updateModalFormDefaultValues,
      }}
    >
      {children}
      <FormProvider {...formProps}>
        <div
          className={`cb-overlay${modalVisibility ? " open" : ""}`}
          onClick={handleOverlayClick}
        >
          <div
            ref={modalRef}
            className={`app-utility-modal ${modalClassName ?? ""} ${modalVariant}`}
            role="dialog"
            aria-modal="true"
            {...((modalTitle && !(modalClassName || "").includes("hide-header"))
              ? { "aria-labelledby": "app-modal-title" }
              : { "aria-label": modalAriaLabel || modalTitle || defaultAriaLabelFor(modalVariant) })}
          >
            <div className="cb-modal">
              {!!ModalPromoComponent && (
                <div className="modal-promo-section">
                  <ModalPromoComponent />
                </div>
              )}
              <div className="modal-dialog-header">
                {(modalVariant === modalVariants.stepByStepDialog || modalStack.length > 1) && (
                  <button
                    className="go-back-button"
                    onClick={onBackButtonClick}
                    disabled={modalVariant === modalVariants.stepByStepDialog && modalStep === 1 && modalStack.length <= 1}
                    aria-label="Go back"
                  >
                    <ArrowLeft />
                  </button>
                )}
                <h3 className="title" id="app-modal-title">{modalTitle}</h3>
                <CloseButton onClick={onModalClose} />
              </div>
              <ModalComponent />
            </div>
          </div>
        </div>
      </FormProvider>
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const modalContextData = useContext(ModalContext);
  if (modalContextData === undefined) {
    throw new Error("useModalConntext must be used within a ModalContextProvider");
  }
  return modalContextData;
};
